import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingBag, Menu, X, Search, Heart } from "lucide-react";
import { LOGO_URL } from "../../data/products";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { totals, bounce } = useCart();
  const { count: wishlistCount } = useWishlist();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [bump, setBump] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (bounce === 0) return;
    setBump(true);
    const t = setTimeout(() => setBump(false), 500);
    return () => clearTimeout(t);
  }, [bounce]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-blush-100 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-8">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="Flownté Clothing"
            className="h-12 w-12 rounded-full object-cover ring-1 ring-blush-200 group-hover:ring-blush-300 transition"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-script text-3xl text-blush-600">Flownté</span>
            <span className="text-[10px] tracking-[0.32em] text-ink-500 font-body uppercase">
              Elegance in Motion
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative font-body text-sm tracking-[0.18em] uppercase transition-colors duration-300 ${
                  isActive ? "text-blush-600" : "text-ink-700 hover:text-blush-600"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span
                    className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-[1.5px] bg-blush-300 transition-all duration-300 ${
                      isActive ? "w-5" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-search-btn"
            className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-700 hover:text-blush-600 hover:bg-blush-50 transition"
            aria-label="Search"
          >
            <Search size={18} strokeWidth={1.5} />
          </button>

          <Link
            to="/wishlist"
            data-testid="nav-wishlist-btn"
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-700 hover:text-blush-600 hover:bg-blush-50 transition"
            aria-label="Wishlist"
          >
            <Heart size={18} strokeWidth={1.5} />
            {wishlistCount > 0 && (
              <span
                data-testid="nav-wishlist-count"
                className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] px-1 rounded-full bg-blush-600 text-white text-[10px] font-body font-medium grid place-items-center"
              >
                {wishlistCount}
              </span>
            )}
          </Link>

          <Link
            to="/cart"
            data-testid="nav-cart-btn"
            className={`relative inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-700 hover:text-blush-600 hover:bg-blush-50 transition ${
              bump ? "scale-110" : "scale-100"
            }`}
            aria-label="Cart"
          >
            <ShoppingBag size={18} strokeWidth={1.5} />
            {totals.count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] px-1 rounded-full bg-blush-600 text-white text-[10px] font-body font-medium grid place-items-center"
              >
                {totals.count}
              </span>
            )}
          </Link>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-700 hover:bg-blush-50"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
          >
            {open ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden backdrop-blur-xl bg-white/90 border-t border-blush-100">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-mobile-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-body tracking-[0.18em] uppercase text-sm ${
                    isActive ? "text-blush-600" : "text-ink-700"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
